import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Layout from "../layouts/Layout";
import {
  API_BASE_URL,
  FONT_HEADING,
  FONT_UI,
  SKIN_THEMES,
} from "../layouts/constants";

const STATUS_OPTIONS = ["planned", "active", "stopped", "abandoned"];

function useSkin() {
  const skinId = localStorage.getItem("skinTheme") || "navy";
  return SKIN_THEMES.find((s) => s.id === skinId) || SKIN_THEMES[0];
}

// Use this for start_time, end_time, updated_at.
// No timezone conversion here.
// Backend already sends/stores the value you want to display.
function formatDBDateTime(value) {
  if (!value) return "-";

  const raw = String(value).trim();
  if (!raw) return "-";

  const clean = raw.replace("T", " ").split(".")[0];
  const [datePart, timePart = "00:00:00"] = clean.split(" ");

  const [yyyy, mm, dd] = datePart.split("-");
  const [hh = "00", mi = "00", ss = "00"] = timePart.split(":");

  if (!yyyy || !mm || !dd) return raw;

  let hour = parseInt(hh, 10);
  const ampm = hour >= 12 ? "PM" : "AM";

  hour = hour % 12;
  if (hour === 0) hour = 12;

  return `${dd}/${mm}/${yyyy} ${String(hour).padStart(2, "0")}:${mi}:${ss} ${ampm}`;
}

function valueOrDash(value) {
  return value === null || value === undefined || value === "" ? "-" : value;
}

function getToken() {
  return (
    localStorage.getItem("token") ||
    localStorage.getItem("authToken") ||
    localStorage.getItem("adminToken")
  );
}

export default function EscortList() {
  const skin = useSkin();
  const navigate = useNavigate();
  const location = useLocation();

  const params = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );

  const [q, setQ] = useState(params.get("q") || "");
  const [status, setStatus] = useState(params.get("status") || "");
  const [page, setPage] = useState(Number(params.get("page") || 1));

  const [rows, setRows] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const [loading, setLoading] = useState(true);
  const [tableLoading, setTableLoading] = useState(false);

  const [alert, setAlert] = useState({ type: "", message: "" });

  const [dashboardData, setDashboardData] = useState({
    company_name: "",
    licensedFor: "",
    admin_active: 0,
    company_address: "",
    company_phone: "",
    admin_email: "",
    company_logo: "",
    user_map: "",
  });

  const recordsPerPage = 10;

  const routePage = useMemo(
    () => (dashboardData.user_map === "google" ? "/route_list" : "/route_list"),
    [dashboardData.user_map]
  );

  const escortPage = useMemo(() => "/escort_master", []);
  const escortTrackingPage = useMemo(() => "/escort_live_tracking", []);

  const authHeaders = () => {
    const token = getToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const buildQueryString = (nextPage = page, nextQ = q, nextStatus = status) => {
    const search = new URLSearchParams();
    search.set("page", String(nextPage));
    search.set("limit", String(recordsPerPage));
    if (nextQ.trim()) search.set("q", nextQ.trim());
    if (nextStatus.trim()) search.set("status", nextStatus.trim());
    return search.toString();
  };

  const updateUrl = (nextPage = page, nextQ = q, nextStatus = status) => {
    const query = buildQueryString(nextPage, nextQ, nextStatus);
    navigate(`/escort_list?${query}`, { replace: false });
  };

  useEffect(() => {
    const token = getToken();
    const isLoggedIn = localStorage.getItem("isLoggedIn");
    const userData = JSON.parse(localStorage.getItem("userData") || "{}");

    if (!token || !isLoggedIn) {
      navigate("/");
      return;
    }

    const local = {
      company_name:
        localStorage.getItem("company_name") || userData.company_name || "",
      licensedFor:
        localStorage.getItem("licensedFor") || userData.licensedFor || "",
      admin_active: Number(
        localStorage.getItem("admin_active") ?? userData.admin_active ?? 0
      ),
      company_address:
        localStorage.getItem("company_address") ||
        userData.company_address ||
        "",
      company_phone:
        localStorage.getItem("company_phone") || userData.company_phone || "",
      admin_email:
        localStorage.getItem("admin_email") || userData.admin_email || "",
      company_logo:
        localStorage.getItem("company_logo") || userData.company_logo || "",
      user_map: localStorage.getItem("user_map") || userData.user_map || "",
    };

    setDashboardData(local);
    fetchPageData(local, token);
  }, [navigate]);

  useEffect(() => {
    setQ(params.get("q") || "");
    setStatus(params.get("status") || "");
    setPage(Number(params.get("page") || 1));
  }, [params]);

  useEffect(() => {
    if (!loading) fetchEscortRuns();
  }, [location.search]);

  useEffect(() => {
    if (!alert.message) return;
    const timer = setTimeout(() => setAlert({ type: "", message: "" }), 5000);
    return () => clearTimeout(timer);
  }, [alert.message]);

  async function fetchPageData(local, token) {
    try {
      setLoading(true);

      const dashboardRes = await fetch(
        `${API_BASE_URL}/dashboard/admin/dashboard`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!dashboardRes.ok) throw new Error("Failed to fetch dashboard");

      const dashboardResult = await dashboardRes.json();
      const apiData = dashboardResult?.data || {};
      const companyDetails = apiData?.companyDetails || {};

      const mergedDashboard = {
        ...local,
        company_name: companyDetails.company_name ?? local.company_name ?? "",
        licensedFor: companyDetails.licensedFor ?? local.licensedFor ?? "",
        admin_active: Number(
          companyDetails.admin_active ?? local.admin_active ?? 0
        ),
        company_address:
          companyDetails.company_address ?? local.company_address ?? "",
        company_phone: companyDetails.company_phone ?? local.company_phone ?? "",
        admin_email: companyDetails.admin_email ?? local.admin_email ?? "",
        company_logo: companyDetails.company_logo ?? local.company_logo ?? "",
        user_map: companyDetails.user_map ?? local.user_map ?? "",
      };

      setDashboardData(mergedDashboard);
      await fetchEscortRuns();
    } catch (error) {
      console.error("Escort list page error:", error);
      setAlert({
        type: "error",
        message: error.message || "Failed to load escort list.",
      });
    } finally {
      setLoading(false);
    }
  }

  async function fetchEscortRuns() {
    try {
      setTableLoading(true);

      const currentParams = new URLSearchParams(window.location.search);
      const currentPage = Number(currentParams.get("page") || page || 1);
      const currentQ = currentParams.get("q") ?? q;
      const currentStatus = currentParams.get("status") ?? status;

      const query = buildQueryString(currentPage, currentQ, currentStatus);

      const res = await fetch(`${API_BASE_URL}/escort/runs?${query}`, {
        method: "GET",
        headers: { "Content-Type": "application/json", ...authHeaders() },
      });

      const data = await res.json();

      if (!res.ok || data.success === false) {
        throw new Error(data.message || "Failed to fetch escort runs");
      }

      const apiRows = Array.isArray(data.data)
        ? data.data
        : Array.isArray(data.rows)
        ? data.rows
        : Array.isArray(data.data?.runs)
        ? data.data.runs
        : [];

      setRows(apiRows);
      setTotalRecords(
        Number(data.totalRecords || data.total_records || apiRows.length || 0)
      );
      setTotalPages(Number(data.totalPages || data.total_pages || 1));
    } catch (err) {
      console.error("Fetch escort runs error:", err);
      setRows([]);
      setTotalRecords(0);
      setTotalPages(1);
      setAlert({
        type: "error",
        message: err.message || "Something went wrong",
      });
    } finally {
      setTableLoading(false);
    }
  }

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setPage(1);
    updateUrl(1, q, status);
  };

  const handleClear = () => {
    setQ("");
    setStatus("");
    setPage(1);
    navigate("/escort_list");
  };

  const handlePageChange = (nextPage) => {
    if (nextPage < 1 || nextPage > totalPages) return;
    setPage(nextPage);
    updateUrl(nextPage, q, status);
  };

  const handleDelete = async (runId) => {
    const ok = window.confirm(
      `Delete escort run #${runId}? This will remove the run and all its points/events.`
    );
    if (!ok) return;

    try {
      const res = await fetch(`${API_BASE_URL}/escort/runs/${runId}`, {
        method: "DELETE",
        headers: { "Content-Type": "application/json", ...authHeaders() },
      });

      const data = await res.json();

      if (!res.ok || data.success === false) {
        throw new Error(data.message || "Delete failed");
      }

      setAlert({
        type: "success",
        message: `Escort run #${runId} deleted successfully.`,
      });
      fetchEscortRuns();
    } catch (err) {
      setAlert({ type: "error", message: err.message || "Delete failed" });
    }
  };

  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const items = [];
    const start = Math.max(1, page - 2);
    const end = Math.min(totalPages, page + 2);

    if (page > 1) {
      items.push(
        <PageButton key="prev" onClick={() => handlePageChange(page - 1)}>
          ‹ Prev
        </PageButton>
      );
    }

    for (let i = start; i <= end; i += 1) {
      items.push(
        <PageButton
          key={i}
          active={i === page}
          onClick={() => handlePageChange(i)}
        >
          {i}
        </PageButton>
      );
    }

    if (page < totalPages) {
      items.push(
        <PageButton key="next" onClick={() => handlePageChange(page + 1)}>
          Next ›
        </PageButton>
      );
    }

    return (
      <div className="flex justify-center items-center gap-2 mt-5 flex-wrap">
        {items}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-100 flex flex-col items-center justify-center">
        <div className="h-10 w-10 rounded-full border-4 border-blue-100 border-t-blue-600 animate-spin mb-3" />
        <p className="text-slate-500" style={{ fontFamily: FONT_UI }}>
          Loading escort runs...
        </p>
      </div>
    );
  }

  return (
    <Layout
      dashboardData={dashboardData}
      routePage={routePage}
      activePage="escort"
      activeMenu="escort"
    >
      <div className="min-h-screen text-slate-900" style={{ fontFamily: FONT_UI }}>
        <PageHeader
          skin={skin}
          onNewEscort={() => navigate(escortPage)}
          onRouteList={() => navigate("/route_list")}
          onCreateRoute={() => navigate(routePage)}
        />

        {alert.message ? <Alert type={alert.type} message={alert.message} /> : null}

        <div className="bg-white border border-slate-100 rounded-2xl overflow-hidden shadow-sm">
          <div className="px-6 py-5 border-b border-slate-200 flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-3 font-bold text-lg text-slate-900">
              <i className="bx bx-list-ul text-blue-600 text-[22px]" />
              <span>All Escort Runs</span>
            </div>

            <form className="flex items-center gap-2 flex-wrap" onSubmit={handleSearchSubmit}>
              <input
                type="text"
                placeholder="Search guard, title, from, to..."
                value={q}
                onChange={(e) => setQ(e.target.value)}
                className="h-10 min-w-[260px] px-3 rounded-xl border border-slate-200 bg-white text-sm text-slate-900 outline-none focus:border-blue-600 focus:ring-4 focus:ring-blue-100"
                style={{ fontFamily: FONT_UI }}
              />

              <select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                className="h-10 px-3 rounded-xl border border-slate-200 bg-white text-sm text-slate-900 outline-none focus:border-blue-600 focus:ring-4 focus:ring-blue-100"
                style={{ fontFamily: FONT_UI }}
              >
                <option value="">All statuses</option>
                {STATUS_OPTIONS.map((item) => (
                  <option key={item} value={item}>
                    {item.charAt(0).toUpperCase() + item.slice(1)}
                  </option>
                ))}
              </select>

              <button
                type="submit"
                className="h-10 px-4 rounded-xl text-white text-sm font-semibold inline-flex items-center justify-center gap-2 shadow-lg shadow-blue-500/20"
                style={{
                  background:
                    skin.gradient ||
                    "linear-gradient(135deg, #1e293b 0%, #2563eb 100%)",
                  fontFamily: FONT_UI,
                }}
              >
                <i className="bx bx-search" /> Search
              </button>
            </form>
          </div>

          <div className="mx-6 my-4 px-4 py-3 rounded-xl bg-slate-50 border border-slate-200 flex items-center gap-2 text-sm text-slate-500">
            <i className="bx bx-info-circle" />
            Total: <strong className="text-slate-900">{totalRecords}</strong> | Page{" "}
            {page} of {totalPages}
          </div>

          <div className="px-6 pb-6 overflow-x-auto">
            {tableLoading ? (
              <EmptyState iconClass="bx bx-loader-alt bx-spin" title="Loading escort runs..." />
            ) : rows.length > 0 ? (
              <>
                <table className="w-full min-w-[1120px] border border-slate-200 rounded-2xl overflow-hidden border-collapse">
                  <thead className="bg-slate-50">
                    <tr>
                      <Th>#</Th>
                      <Th>Run / Title</Th>
                      <Th>Guard</Th>
                      <Th>Vehicle</Th>
                      <Th>From → To</Th>
                      <Th>Status</Th>
                      <Th>Start Time (IST)</Th>
                      <Th>End Time (IST)</Th>
                      <Th>Last Update (IST)</Th>
                      <Th>Points</Th>
                      <Th>Distance (m)</Th>
                      <Th>Actions</Th>
                    </tr>
                  </thead>

                  <tbody>
                    {rows.map((row, index) => {
                      const runId = row.run_id;
                      const badge = String(row.status || "unknown").toLowerCase();
                      const serial = (page - 1) * recordsPerPage + index + 1;

                      return (
                        <tr key={runId || index} className="hover:bg-slate-50">
                          <Td>{serial}</Td>

                          <Td>
                            <strong>#{valueOrDash(runId)}</strong>
                            <div className="text-xs text-slate-500 mt-0.5">
                              {valueOrDash(row.title)}
                            </div>
                          </Td>

                          <Td>
                            <div className="text-sm font-medium text-slate-800">
                              {valueOrDash(row.guard_name)}
                            </div>
                            <div className="font-mono text-xs text-slate-400 mt-0.5">
                              {valueOrDash(row.member_id)}
                            </div>
                          </Td>

                          <Td>
                            <span className="font-mono text-xs text-slate-600">
                              {valueOrDash(row.vehicle)}
                            </span>
                          </Td>

                          <Td>
                            <div className="flex items-center gap-1 text-sm">
                              <i className="bx bx-current-location text-blue-500" />
                              {valueOrDash(row.from_name)}
                            </div>
                            <div className="flex items-center gap-1 text-sm mt-0.5">
                              <i className="bx bx-navigation text-green-500" />
                              {valueOrDash(row.to_name)}
                            </div>
                          </Td>

                          <Td>
                            <StatusBadge status={badge} />
                          </Td>

                          <Td>
                            <span className="font-mono text-xs text-slate-600">
                              {formatDBDateTime(row.start_time)}
                            </span>
                          </Td>

                          <Td>
                            <span className="font-mono text-xs text-slate-600">
                              {formatDBDateTime(row.end_time)}
                            </span>
                          </Td>

                          <Td>
                            <span className="font-mono text-xs text-slate-400">
                              {formatDBDateTime(
                                row.updated_at ||
                                  row.last_update_time ||
                                  row.last_updated_at ||
                                  row.created_at
                              )}
                            </span>
                          </Td>

                          <Td>{Number(row.points_count || 0)}</Td>
                          <Td>{Number(row.distance_m || 0)}</Td>

                          <Td>
  <div className="flex items-center gap-2">

    {/* Always show Track button */}
    <IconButton
      title="Track on Map"
      className="bg-green-600 hover:bg-green-700"
      onClick={() =>
        navigate(
          `${escortTrackingPage}?member_id=${encodeURIComponent(
            row.member_id || ""
          )}&run_id=${runId}`
        )
      }
    >
      <IconSvg type="map" />
    </IconButton>

    {/* Hide Edit/Delete when Active or Stopped */}
    {!["active", "stopped"].includes(
      String(row.status || "").toLowerCase()
    ) && (
      <>
        <IconButton
          title="Edit"
          className="bg-blue-600 hover:bg-blue-700"
          onClick={() => navigate(`${escortPage}?edit_id=${runId}`)}
        >
          <IconSvg type="edit" />
        </IconButton>

        <IconButton
          title="Delete"
          className="bg-red-600 hover:bg-red-700"
          onClick={() => handleDelete(runId)}
        >
          <IconSvg type="trash" />
        </IconButton>
      </>
    )}
  </div>
</Td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>

                {renderPagination()}
              </>
            ) : (
              <EmptyState iconClass="bx bx-map" title="No Escort Runs Found" />
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}

function PageHeader({ skin, onNewEscort, onRouteList, onCreateRoute }) {
  return (
    <>
      <div className="mb-6">
        <h2
          className="m-0 text-[26px] font-normal text-slate-900 tracking-tight"
          style={{ fontFamily: FONT_HEADING }}
        >
          Escort Runs
        </h2>
      </div>

      <div className="bg-white rounded-2xl px-7 py-6 shadow-sm border border-slate-100 mb-6 flex justify-between items-center gap-4 flex-wrap">
        <div>
          <h3
            className="m-0 text-[22px] font-normal text-slate-900"
            style={{ fontFamily: FONT_HEADING }}
          >
            Escort Route List
          </h3>
          <p className="mt-1 text-sm text-slate-500" style={{ fontFamily: FONT_UI }}>
            Search, view, edit, track, and delete escort run records.
          </p>
        </div>

        <div className="flex gap-3 flex-wrap">
          <button
            type="button"
            className="h-10 px-4 rounded-xl text-white text-sm font-semibold"
            style={{ background: skin.gradient, fontFamily: FONT_UI }}
            onClick={onNewEscort}
          >
            New Escort Run
          </button>

          <button
            type="button"
            className="h-10 px-4 rounded-xl border border-slate-200 bg-white text-slate-700 text-sm font-semibold"
            style={{ fontFamily: FONT_UI }}
            onClick={onCreateRoute}
          >
            Create Route
          </button>

          <button
            type="button"
            className="h-10 px-4 rounded-xl border border-slate-200 bg-white text-slate-700 text-sm font-semibold"
            style={{ fontFamily: FONT_UI }}
            onClick={onRouteList}
          >
            Route List
          </button>
        </div>
      </div>
    </>
  );
}

function Alert({ type, message }) {
  const isSuccess = type === "success";

  return (
    <div
      className={`mb-5 px-4 py-3.5 rounded-xl text-sm font-medium border flex items-center gap-2 ${
        isSuccess
          ? "bg-green-50 border-green-200 text-green-700"
          : "bg-red-50 border-red-200 text-red-700"
      }`}
      style={{ fontFamily: FONT_UI }}
    >
      {message}
    </div>
  );
}

function Th({ children }) {
  return (
    <th
      className="text-left px-3 py-3.5 text-xs font-semibold text-slate-600 border-b border-slate-200 whitespace-nowrap bg-slate-50"
      style={{ fontFamily: FONT_UI }}
    >
      {children}
    </th>
  );
}

function Td({ children }) {
  return (
    <td
      className="px-3 py-3.5 text-sm border-b border-slate-200 align-middle"
      style={{ fontFamily: FONT_UI }}
    >
      {children}
    </td>
  );
}

function StatusBadge({ status }) {
  const cls =
    status === "active"
      ? "bg-green-100 text-green-700 border border-green-200"
      : status === "planned"
      ? "bg-blue-100 text-blue-700 border border-blue-200"
      : status === "stopped"
      ? "bg-orange-100 text-orange-700 border border-orange-200"
      : status === "abandoned"
      ? "bg-red-100 text-red-700 border border-red-200"
      : "bg-slate-100 text-slate-700 border border-slate-200";

  return (
    <span className={`px-2.5 py-1 rounded-full text-xs font-bold inline-flex items-center gap-1 ${cls}`}>
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  );
}

function IconSvg({ type }) {
  const commonProps = {
    className: "w-4 h-4",
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: "2.2",
    strokeLinecap: "round",
    strokeLinejoin: "round",
    "aria-hidden": "true",
  };

  if (type === "edit") {
    return (
      <svg {...commonProps}>
        <path d="M12 20h9" />
        <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
      </svg>
    );
  }

  if (type === "map") {
    return (
      <svg {...commonProps}>
        <path d="M9 18l-6 3V6l6-3 6 3 6-3v15l-6 3-6-3z" />
        <path d="M9 3v15" />
        <path d="M15 6v15" />
      </svg>
    );
  }

  return (
    <svg {...commonProps}>
      <path d="M3 6h18" />
      <path d="M8 6V4h8v2" />
      <path d="M19 6l-1 14H6L5 6" />
      <path d="M10 11v5" />
      <path d="M14 11v5" />
    </svg>
  );
}

function IconButton({ children, className = "", title = "", ...props }) {
  return (
    <button
      type="button"
      title={title}
      aria-label={title}
      {...props}
      className={`w-9 h-9 rounded-xl text-white inline-flex items-center justify-center transition-colors focus:outline-none focus:ring-4 focus:ring-slate-200 ${className}`}
    >
      {children}
    </button>
  );
}

function PageButton({ children, active = false, ...props }) {
  return (
    <button
      type="button"
      {...props}
      className={`min-w-9 h-9 px-3 rounded-xl border text-sm font-bold transition-colors ${
        active
          ? "bg-blue-600 text-white border-blue-600"
          : "bg-white text-slate-900 border-slate-200 hover:bg-slate-50"
      }`}
      style={{ fontFamily: FONT_UI }}
    >
      {children}
    </button>
  );
}

function EmptyState({ iconClass, title }) {
  return (
    <div className="min-h-[260px] border border-dashed border-slate-200 rounded-2xl flex flex-col items-center justify-center text-center p-8 text-slate-500">
      <i className={`${iconClass} text-5xl text-slate-300 mb-3`} />
      <h3 className="m-0 mb-2 text-xl text-slate-900" style={{ fontFamily: FONT_HEADING }}>
        {title}
      </h3>
    </div>
  );
}